
import React, { useCallback, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography, Chip, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import { IconRefresh, IconBuilding, IconUser, IconPlus } from '@tabler/icons-react';
import { CustomerIndexItemDto } from '@techgitter/contracts';
import { useCustomerLookup } from '../hooks/useCustomerLookup';

const ADD_NEW_CUSTOMER_ID = '__add_new_customer__';

// Conform the async customer dropdown to the unified dropdown look: rounded card
// + hairline-ring shadow, padded list, rounded #f5f7fa hover (#f0f4f9 selected),
// replacing MUI's larger options and grey full-width block hover. The `&&`
// doubles the Paper-class specificity so these win over MUI's listbox-scoped
// option styles (a single class tied and lost on an earlier attempt). Plain
// object (not `satisfies SxProps`) per the design-system Babel gotcha.
const DROPDOWN_PAPER_SX = {
  mt: '4px',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': { padding: '4px' },
  '&& .MuiAutocomplete-option': {
    borderRadius: '5px',
    padding: '6px 10px',
    minHeight: 'auto',
  },
  '&& .MuiAutocomplete-option:hover': { backgroundColor: '#f5f7fa' },
  '&& .MuiAutocomplete-option.Mui-focused': { backgroundColor: '#f5f7fa' },
  '&& .MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: '#f0f4f9' },
  '&& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': { backgroundColor: '#eaf0f7' },
};

interface CustomerLookupControlProps {
  value: CustomerIndexItemDto | null;
  onChange: (value: CustomerIndexItemDto | null) => void;
  onGeneratedAtChange?: (generatedAtUtc: string | null) => void;
  onTabAway?: () => void;
  onAddNew?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
}

export const CustomerLookupControl: React.FC<CustomerLookupControlProps> = ({
  value,
  onChange,
  onGeneratedAtChange,
  onTabAway,
  onAddNew,
  autoFocus = false,
  disabled = false,
}) => {
  const {
    query,
    setQuery,
    results,
    isLoading,
    error,
    retry,
    selectCustomer,
    generatedAtUtc,
  } = useCustomerLookup();

  const handleChange = useCallback((_event: any, newValue: CustomerIndexItemDto | null) => {
    if (newValue && newValue.dataverseCustomerId === ADD_NEW_CUSTOMER_ID) {
      onAddNew?.();
      return;
    }
    selectCustomer(newValue);
    onChange(newValue);
  }, [selectCustomer, onChange, onAddNew]);

  const optionsWithAddNew = useMemo<CustomerIndexItemDto[]>(() => {
    if (!onAddNew) return results;
    const sentinel: CustomerIndexItemDto = {
      dataverseCustomerId: ADD_NEW_CUSTOMER_ID,
      repairShoprCustomerId: '',
      displayName: 'Add new customer',
      customerType: 'Residential',
    };
    return [...results, sentinel];
  }, [results, onAddNew]);

  const handleInputChange = useCallback((_event: any, newInputValue: string) => {
    setQuery(newInputValue);
  }, [setQuery]);

  useEffect(() => {
    if (onGeneratedAtChange) {
      onGeneratedAtChange(generatedAtUtc);
    }
  }, [generatedAtUtc, onGeneratedAtChange]);

  const getSecondaryText = (item: CustomerIndexItemDto): string => {
    if (item.email) return item.email;
    if (item.mobile) return item.mobile;
    if (item.phone) return item.phone;
    if (item.domains) {
      const firstDomain = item.domains.split(',')[0].trim();
      return firstDomain;
    }
    return '';
  };

  return (
    <Autocomplete
      size="small"
      value={value}
      onChange={handleChange}
      inputValue={query}
      onInputChange={handleInputChange}
      options={optionsWithAddNew}
      getOptionLabel={(option) => (option.dataverseCustomerId === ADD_NEW_CUSTOMER_ID ? '' : option.displayName)}
      isOptionEqualToValue={(option, value) => option.dataverseCustomerId === value.dataverseCustomerId}
      loading={isLoading}
      disabled={disabled}
      autoHighlight
      autoSelect
      filterOptions={(x) => x}
      slotProps={{ paper: { sx: DROPDOWN_PAPER_SX } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Customer"
          placeholder="Search by name, email, phone, or domain..."
          autoFocus={autoFocus}
          error={!!error}
          helperText={error || undefined}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              params.inputProps?.onKeyDown?.(event);
              if (event.key === 'Tab' && !event.shiftKey && onTabAway) {
                setTimeout(() => onTabAway(), 0);
              }
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={20} />}
                {error && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={retry} edge="end">
                      <IconRefresh size={18} stroke={1.5} />
                    </IconButton>
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        if (option.dataverseCustomerId === ADD_NEW_CUSTOMER_ID) {
          return (
            <li {...props} key={ADD_NEW_CUSTOMER_ID}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, color: 'primary.main', fontWeight: 600 }}>
                <IconPlus size={16} stroke={1.5} />
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  Add new customer
                </Typography>
              </Box>
            </li>
          );
        }
        const secondaryText = getSecondaryText(option);
        const icon = option.customerType === 'Business'
          ? <IconBuilding size={16} stroke={1.5} />
          : <IconUser size={16} stroke={1.5} />;

        return (
          <li {...props} key={option.dataverseCustomerId}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                {icon}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>
                  {option.displayName}
                </Typography>
                {secondaryText && (
                  <Typography color="text.secondary" noWrap sx={{ fontSize: 11 }}>
                    {secondaryText}
                  </Typography>
                )}
              </Box>
              <Chip
                label={option.customerType}
                size="small"
                variant="outlined"
                sx={{ ml: 'auto' }}
              />
            </Box>
          </li>
        );
      }}
      sx={{ minWidth: 300 }}
    />
  );
};
